// @ts-nocheck
import React, { useState, useEffect, FC } from "react";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { Canvas } from "react-three-fiber";
import { observer } from "mobx-react-lite";
import { DoubleSide } from "three";
import {
  euclideanDistance,
  getVerticesAndFaces,
  IVerticesAndFaces,
  getCircleHornVertices,
} from "./functions";
import { IHornDesignResponse } from "../../interfaces";
import { CircleHornGeometry } from "./HornGeometry";
import { GEOMETRY } from "../../constants/enums";
import { EllipseHornGeometry } from "./EllipseHornGeometry";

interface IHornProps {
  output: IHornDesignResponse;
  geometry?: number;
  landing?: Boolean;
  material?: any;
}

const CircularHorn: FC<IHornProps> = ({ output, material }) => {
  const [geometry, setGeometry] = useState<CircleHornGeometry>();

  useEffect(() => {
    const edgeVertices = getCircleHornVertices(output);

    const lastPoint = edgeVertices[edgeVertices.length - 1];
    const firstPoint = edgeVertices[0];

    const geo = new CircleHornGeometry(
      euclideanDistance(lastPoint?.z, lastPoint?.y),
      euclideanDistance(firstPoint?.z, firstPoint?.y),
      output.hXval[output.hXval.length - 1],
      30,
      edgeVertices.length - 1,
      edgeVertices
    );
    geo.computeVertexNormals();
    setGeometry(geo);
  }, [output]);

  return geometry ? (
    <mesh receiveShadow castShadow>
      <cylinderBufferGeometry {...geometry} />
      {material}
    </mesh>
  ) : null;
};

const EllipticalHorn: FC<IHornProps> = ({ output, material }) => {
  const [geometry, setGeometry] = useState<CircleHornGeometry>();

  useEffect(() => {
    const edgeVertices = getCircleHornVertices(output, true);
    const geo = new EllipseHornGeometry(
      30,
      edgeVertices.length - 1,
      edgeVertices,
      output
    );

    geo.computeVertexNormals();
    setGeometry(geo);
  }, [output]);

  return geometry ? (
    <mesh receiveShadow castShadow>
      <cylinderBufferGeometry {...geometry} />
      {material}
    </mesh>
  ) : null;
};

export const RectangularHorn: FC<IHornProps> = observer(
  ({ output, material }) => {
    const [data, setData] = useState<IVerticesAndFaces>();

    useEffect(() => {
      const { hCornerRad, hWidth, hHeight, hXval } = output || {};
      if (hCornerRad && hWidth && hHeight && hXval) {
        setData(getVerticesAndFaces(output));
      }
    }, [output]);

    return data ? (
      <>
        <mesh receiveShadow castShadow>
          <boxGeometry
            onUpdate={(self) => {
              self.computeFaceNormals();
            }}
            attach="geometry"
            vertices={data?.edgeVertices}
            faces={data?.edgeFaces}
          />
          {material}
        </mesh>
        <mesh receiveShadow castShadow>
          <boxGeometry
            onUpdate={(self) => {
              self.computeFaceNormals();
            }}
            attach="geometry"
            vertices={data?.fanVertices}
            faces={data?.fanFaces}
          />
          {material}
        </mesh>
      </>
    ) : null;
  }
);

export const Horn: FC<IHornProps> = observer(
  ({ output, geometry, landing }) => {
    const material = landing ? (
      <meshStandardMaterial
        attach="material"
        color={0xda0017}
        side={DoubleSide}
        metalness={0.9}
        roughness={0.2}
      />
    ) : (
      <meshNormalMaterial
        attach="material"
        color={0x005e99}
        side={DoubleSide}
      />
    );

    if (geometry === GEOMETRY.Circular)
      return <CircularHorn material={material} output={output} />;
    if (geometry === GEOMETRY.Elliptical)
      return <EllipticalHorn material={material} output={output} />;
    return <RectangularHorn material={material} output={output} />;
  }
);
